import { isPlainObject, times } from 'lodash';
import { conditionHolds, type ReadField } from '../conditions';
import { fieldTypeOf, settingsOf } from '../field-types';
import type { FieldTypeCard } from '../field-types/card';
import { childrenOf, fieldsOf, holdsFields, optionsOf } from '../format/elements';
import type {
  Element,
  FieldElement,
  FormDefinition,
  RepeatableGroupField,
  SubmissionValues,
} from '../format/form-definition';
import { placesOf } from '../format/references';
import {
  firstError,
  isFilled,
  resolveValidations,
  type ValidationContext,
  type ResolvedRangeValidation,
} from '../validations';

type EnteredValues = { [fieldId: string]: unknown };

interface FieldNow {
  field: FieldElement;
  path: string;
  shown: boolean;
  required: boolean;
  range: ResolvedRangeValidation | undefined;
  error: string | undefined;
}

export interface FormNow {
  shown: Map<string, boolean>;
  fields: Map<string, FieldNow>;
  values: SubmissionValues;
}

export const pathIn = (rowPath: string | undefined, id: string): string => {
  return rowPath === undefined ? id : `${rowPath}.${id}`;
};

interface Row {
  group: RepeatableGroupField;
  groupPath: string;
  path: string;
  index: number;
  values: EnteredValues;
}

interface Placed {
  field: FieldElement;
  card: FieldTypeCard;
  path: string;
  row: Row | undefined;
  shown: boolean;
  value: unknown;
  rows: Row[];
}

export const formNow = (definition: FormDefinition, entered: EnteredValues, today: string): FormNow => {
  const places = placesOf(definition);
  const placed = new Map<string, Placed>();
  const shown = new Map<string, boolean>();

  const referredPath = (fieldId: string, row: Row | undefined) =>
    row !== undefined && places.get(fieldId)?.group?.id === row.group.id ? pathIn(row.path, fieldId) : fieldId;
  const readFrom =
    (row: Row | undefined): ReadField =>
    fieldId => {
      const other = placed.get(referredPath(fieldId, row));
      return other?.shown
        ? { id: fieldId, card: other.card, value: other.value, options: optionsOf(other.field) }
        : undefined;
    };

  const walk = (elements: Element[], parentShown: boolean, row: Row | undefined): void => {
    for (const element of elements) {
      const path = pathIn(row?.path, element.id);
      const visible =
        parentShown && (element.visibleWhen === undefined || conditionHolds(element.visibleWhen, readFrom(row), today));
      shown.set(path, visible);
      if (element.kind !== 'field') {
        walk(childrenOf(element), visible, row);
        continue;
      }
      const value = (row ? row.values : entered)[element.id];
      const rows = holdsFields(element)
        ? rowValues(value).map((values, index): Row => ({
            group: element,
            groupPath: path,
            path: `${path}.${index}`,
            index,
            values,
          }))
        : [];
      placed.set(path, {
        field: element,
        card: fieldTypeOf(element),
        path,
        row,
        shown: visible,
        value: visible ? value : undefined,
        rows,
      });
      for (const each of rows) walk(childrenOf(element), visible, each);
    }
  };
  walk(definition.elements, true, undefined);

  const rowsAbove = ({ row, field }: Placed): unknown[] | undefined =>
    row &&
    times(row.index, index => {
      const other = placed.get(pathIn(`${row.groupPath}.${index}`, field.id));
      return other?.shown ? other.value : undefined;
    });

  const fields = new Map<string, FieldNow>();
  for (const field of placed.values()) {
    const read = readFrom(field.row);
    const moment: ValidationContext = {
      holds: condition => conditionHolds(condition, read, today),
      fieldValue: fieldId => read(fieldId)?.value,
      today,
    };
    const validations = resolveValidations(definition, field.field, moment);
    fields.set(field.path, {
      field: field.field,
      path: field.path,
      shown: field.shown,
      required: validations.some(validation => validation.kind === 'required'),
      range: validations.find((validation): validation is ResolvedRangeValidation => validation.kind === 'range'),
      error: field.shown
        ? firstError({ field: field.field, value: field.value, validations, rowsAbove: rowsAbove(field) })
        : undefined,
    });
  }

  return {
    shown,
    fields,
    values: submission(placed, definition.elements, undefined) as SubmissionValues,
  };
};

const submission = (
  placed: Map<string, Placed>,
  elements: Element[],
  row: Row | undefined,
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  for (const element of fieldsOf(elements)) {
    const field = placed.get(pathIn(row?.path, element.id));
    if (!field?.shown) continue;
    values[element.id] = holdsFields(element)
      ? field.rows.map(each => submission(placed, element.elements, each))
      : isFilled(field.card, field.value)
        ? stored(field.card, element, field.value)
        : structuredClone(field.card.emptyValue);
  }
  return values;
};

const stored = (card: FieldTypeCard, field: FieldElement, value: NonNullable<unknown>): unknown => {
  return card.stored ? card.stored(value, settingsOf(field)) : value;
};

const rowValues = (value: unknown): EnteredValues[] => {
  if (!Array.isArray(value)) return [];
  return value.map((row: unknown) => (isPlainObject(row) ? (row as EnteredValues) : {}));
};
